import React, { useRef, useState } from 'react';
import { Button, Form, Message, Modal } from 'semantic-ui-react';
import styled from 'styled-components';
import PositionedModal from 'common/components/PositionedModal';
import { useToasts } from 'common/toast';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getCurrentOrganizationId } from 'common/selectors/auth';
import { teamApi } from '../api/teamApi';

interface ImportTeamModalProps {
  open: boolean;
  onClose: () => void;
}

const ImportTeamModal: React.FC<ImportTeamModalProps> = ({ open, onClose }) => {
  const { addToast } = useToasts();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  const navigate = useNavigate();
  const organizationId = useSelector(getCurrentOrganizationId);
  const [validateTeamImport] = teamApi.useValidateTeamImportMutation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
      setErrors([]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setErrors(['Please select a CSV file to import']);
      return;
    }

    setIsValidating(true);
    setErrors([]);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const result = await validateTeamImport(formData).unwrap();
      navigate('/app/teams/import-review', {
        state: {
          results: result,
          file: selectedFile,
        },
      });
      onClose();
    } catch (error: any) {
      console.error('Team import validation failed:', error);
      setErrors([error?.data?.error || 'Failed to validate file.']);
      addToast('Validation failed', { appearance: 'error', autoDismiss: true });
    } finally {
      setIsValidating(false);
    }
  };

  const isLoading = isValidating;

  return (
    <PositionedModal open={open} onClose={onClose} size="small" data-testid="import-team-modal">
      <Modal.Header>Team Import / Update</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field>
            <H4>
              Upload a CSV file to validate and then import or update your team hierarchy.
            </H4>
          </Form.Field>
          <Form.Field>
            <FileUploadContainer>
              <FileInput
                id="team-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                ref={fileInputRef}
                disabled={isLoading || !organizationId}
              />
              <FileInputButton htmlFor="team-file" disabled={isLoading || !organizationId}>
                Choose file
              </FileInputButton>
              <FileName>{selectedFile?.name || 'No file selected'}</FileName>
            </FileUploadContainer>
          </Form.Field>

          {errors.length > 0 && (
            <Message negative>
              <Message.Header>Import Errors</Message.Header>
              <ErrorList>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ErrorList>
            </Message>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSubmit}
          loading={isLoading}
          disabled={!selectedFile || !organizationId}
        >
          Import CSV
        </Button>
      </Modal.Actions>
    </PositionedModal>
  );
};

const FileUploadContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputButton = styled.label<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  padding: 0.75rem 1.2rem;
  background: #2185d0;
  color: white;
  font-weight: 700;
  border-radius: 0.35rem;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  border: none;
  text-align: center;
  user-select: none;
`;

const FileName = styled.span`
  color: #333;
  font-size: 0.95rem;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ErrorList = styled.ul`
  margin: 0;
  padding-left: 1.5em;
`;

const H4 = styled.h4`
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
`;

export default ImportTeamModal;
